import { useCallback, useEffect, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Clock, PlayCircle } from "lucide-react";
import { toast } from "sonner";

export function ShiftBar({ branchId }) {
  const [shift, setShift] = useState(null);
  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState("0");
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(() => {
    if (!branchId) return Promise.resolve();
    return api
      .get(`/pos/cashier-shifts?status=open&branch_id=${branchId}`)
      .then((r) => setShift((r.data || [])[0] || null))
      .catch(() => setShift(null));
  }, [branchId]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    if (!branchId) return;
    setSubmitting(true);
    try {
      await api.post("/pos/cashier-shifts/open", { branch_id: branchId, opening_cash: Number(opening) || 0 });
      toast.success("Đã mở ca thu ngân");
      setOpen(false);
      setOpening("0");
      refresh();
    } catch (e) {
      toast.error(parseApiError(e, "Lỗi mở ca").message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center gap-2 text-xs" data-testid="pos-shift-bar">
      <Clock className="w-3.5 h-3.5 text-stone-600" aria-hidden="true" />
      {shift ? (
        <span className="text-stone-700">
          Ca mở • <span className="font-mono">{String(shift.id).slice(0, 8)}</span>
        </span>
      ) : (
        <>
          <span className="text-amber-800">Chưa mở ca</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs rounded-sm"
            onClick={() => setOpen(true)}
            disabled={!branchId}
            data-testid="btn-open-shift"
          >
            <PlayCircle className="w-3 h-3 mr-1" aria-hidden="true" />Mở ca
          </Button>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Mở ca thu ngân</DialogTitle>
            <DialogDescription>Nhập tiền đầu ca để bắt đầu nhận thanh toán tiền mặt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-semibold" htmlFor="shift-opening-cash">Tiền đầu ca (₫)</label>
            <Input
              id="shift-opening-cash"
              type="number"
              min="0"
              value={opening}
              onChange={(e) => setOpening(e.target.value)}
              className="h-9 rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button type="button" onClick={submit} disabled={submitting} className="rounded-sm bg-emerald-800 hover:bg-emerald-900">
              {submitting ? "Đang mở..." : "Xác nhận mở ca"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
